import React, { useState, useEffect, useContext, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CButton, CCol, CContainer, CDatePicker, CFormSwitch, CRow, CModal,
  CModalBody, CModalFooter, CDropdown, CDropdownToggle, CDropdownMenu, CDropdownItem, CTooltip
} from '@coreui/react-pro'
import { Spinner } from 'src/components/spinner/Spinner'
import CollapsibleTree from 'src/components/collapsibleTree/CollapsibleTree'
import dateFormat from 'dateformat'
import { GlobalVariablesContext } from 'src/contexts/globalVariableContext'
import {
  getTender,
  updateTender,
  notifyManufacturers,
  obtainTenderOwnership,
  inheritPreferencesToTender,
  clearAssignmentsOfChapter
} from 'src/services/requests/tenders/tenders.js'
import { getAllUsersShallow } from 'src/services/requests/users/users'
import CIcon from '@coreui/icons-react'
import { cilPen, cilTrash } from '@coreui/icons'
import { useLocation } from 'react-router-dom'
import FeedbackToast from './components/FeedbackToast'
import { AuthContext } from 'src/contexts/authContext'
import jwt from 'jwt-decode'

const TenderView = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const auth = useContext(AuthContext)
  const { hash, setHash, setError, setTenderName, setTenderId, tenderId, tenderChapters } =
    useContext(GlobalVariablesContext)

  const [isLoading, setIsLoading] = useState(false)
  const [toastVisible, setToastVisible] = useState(false)
  const [tenderData, setTenderData] = useState()
  const [offerDueDatePickerVisible, setOfferDueDatePickerVisible] = useState(false)
  const [newOfferDueDate, setNewOfferDueDate] = useState('')
  const [supplierDueDatePickerVisible, setSupplierDueDatePickerVisible] = useState(false)
  const [newSupplierDueDate, setNewSupplierDueDate] = useState('')
  const [hasAccordionItemsVisible, setHasAccordionItemsVisible] = useState(false)
  const [hgcRelevantContentVisible, setHGCRelevantContentVisible] = useState(false)
  const [hasContentToDoubleCheckItemsVisible, setHasContentToDoubleCheckItemsVisible] = useState(false)
  const [hasSpecialElementsVisible, setHasSpecialElementsVisible] = useState(false)
  const [userIsOwner, setUserIsOwner] = useState(false)
  const [changeOwnershipModalVisible, setChangeOwnershipModalVisible] = useState(false)
  const [currentOwnerFullName, setCurrentOwnerFullName] = useState('')
  const [currentSignedInFullName, setCurrentSignedInFullName] = useState('')
  const [transferOwnershipModalContent, setTransferOwnershipModalContent] = useState('')
  const [authInfo, setAuthInfo] = useState('')
  const [toastIsError, setToastIsError] = useState(false)
  const [sendRequestsButtonDisabled, setSendRequestsButtonDisabled] = useState(false)
  const [users, setUsers] = useState(null)
  const [usersLoading, setUsersLoading] = useState(false)
  const [customMessage, setCustomMessage] = useState()
  const [activeChapter, setActiveChapter] = useState()
  const [showingPreferencesOfUser, setShowingPreferencesOfUser] = useState('')

  const fetchUsers = useCallback(() => {
    setUsersLoading(true)
    getAllUsersShallow()
      .then((result) => {
        const sortedUsers = result.data.sort((a, b) => a.fullName.localeCompare(b.fullName, undefined, { sensitivity: 'base' }))
        setUsers(sortedUsers)
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => setUsersLoading(false))
  }, [])

  const clearAssignmentsOfActiveChapter = useCallback(() => {
    setIsLoading(true)
    clearAssignmentsOfChapter(activeChapter)
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        setIsLoading(false)
        fetchTenderData(tenderId)
      })
  }, [activeChapter, tenderId])

  const inheritFromUser = useCallback((fromUser) => {
    setIsLoading(true)
    inheritPreferencesToTender(tenderId, fromUser)
      .catch((err) => {
        console.error(err)
        setCustomMessage(t('TenderView.Error.UserNotCorrectConfig'))
        setToastIsError(true)
        setToastVisible(true)
      })
      .finally(() => {
        setIsLoading(false)
        fetchTenderData(tenderId)
      })
  }, [tenderId, t])

  const fetchTenderData = useCallback((id) => {
    setIsLoading(true)
    setUserIsOwner(true)
    getTender(id).then((result) => {
      setTenderData(result.data)
    }).catch((err) => {
      if (err?.response?.status === 403) {
        setUserIsOwner(false)
        setCurrentOwnerFullName(err.response.data.currentOwnerFullName)
      }
    }).finally(() => setIsLoading(false))
  }, [])

  const getTenderOwnwership = useCallback((id) => {
    setIsLoading(true)
    setUserIsOwner(true)
    obtainTenderOwnership(id).then((result) => {
      fetchTenderData(id)
    }).catch((err) => {
      console.error(err?.response?.data)
    }).finally(() => setIsLoading(false))
  }, [fetchTenderData])

  useEffect(() => {
    if (location.hash) {
      const tenderIdFromUrl = window.location.href.split('/').pop().split('#')[0]
      setTenderId(tenderIdFromUrl)
    } else {
      const tenderIdFromUrl = window.location.href.split('/').pop()
      setTenderId(tenderIdFromUrl)
    }
  }, [location])

  useEffect(() => {
    if (auth.authStatus === 'SignedIn' && auth.sessionInfo.idToken) {
      setAuthInfo(jwt(auth.sessionInfo.idToken))
    }
  }, [auth])

  useEffect(() => {
    setCurrentSignedInFullName(`${authInfo?.first_name} ${authInfo?.last_name}`)
  }, [authInfo])

  useEffect(() => {
    if (tenderId) fetchTenderData(tenderId)
  }, [tenderId, fetchTenderData])

  useEffect(() => {
    setTenderName(tenderData?.hgcSalesforceProject)
    const shouldDisableButton = (
      (tenderData?.status === 'emails_sent' && true) ||
      (tenderData?.status === 'in_progress' && true) ||
      tenderData?.offerDueDate === null ||
      (tenderData?.supplierDueDate === null && true) ||
      tenderData?.offerDueDate < tenderData?.supplierDueDate
    )
    setSendRequestsButtonDisabled(shouldDisableButton)
  }, [tenderData])

  useEffect(() => {
    if (tenderChapters) setHash('#' + tenderChapters[0]?.name)
  }, [tenderChapters])

  useEffect(() => {
    if (userIsOwner) {
      setChangeOwnershipModalVisible(false)
    } else {
      const content = t('TransferOwnershipModal.Content')
        .replaceAll('CURRENT_OWNER', currentOwnerFullName)
        .replaceAll('CURRENT_USER', currentSignedInFullName)
      setTransferOwnershipModalContent(content)
      setChangeOwnershipModalVisible(true)
    }
  }, [userIsOwner, currentOwnerFullName, currentSignedInFullName, t])

  useEffect(() => {
    if (!toastVisible) setToastIsError(false)
  }, [toastVisible])

  useEffect(() => {
    if (users === null) fetchUsers()
  }, [users, fetchUsers])

  useEffect(() => {
    if (users && tenderData) {
      const userSelected = users.find((user) => user.id === tenderData.showWithPreferencesOfUserId)
      setShowingPreferencesOfUser(userSelected.fullName)
    }
  }, [users, tenderData])

  const editOfferDueDateHgc = () => {
    setOfferDueDatePickerVisible(!offerDueDatePickerVisible)
    setNewOfferDueDate('')
  }

  const updateOfferDueDateHgc = async (date) => {
    return new Promise((resolve) => {
      updateTender(tenderId, { offerDueDate: date })
        .then((response) => {
          resolve(response)
        })
        .catch((exception) => {
          setError(exception?.response?.data?.error)
        })
        .finally(() => {
          fetchTenderData(tenderId)
          setOfferDueDatePickerVisible(!offerDueDatePickerVisible)
        })
    })
  }

  const editSupplierDueDate = () => {
    setSupplierDueDatePickerVisible(!supplierDueDatePickerVisible)
    setNewSupplierDueDate('')
  }

  const updateSupplierDueDate = async (date) => {
    return new Promise((resolve) => {
      updateTender(tenderId, { supplierDueDate: date })
        .then((response) => {
          resolve(response)
        })
        .catch((exception) => {
          setError(exception?.response?.data?.error)
        })
        .finally(() => {
          fetchTenderData(tenderId)
          setSupplierDueDatePickerVisible(!supplierDueDatePickerVisible)
          setNewSupplierDueDate('')
        })
    })
  }

  const notifySuppliers = async () => {
    return new Promise((resolve) => {
      setIsLoading(true)
      setSendRequestsButtonDisabled(true)
      notifyManufacturers(tenderId)
        .then((response) => {
          resolve(response)
        })
        .catch((exception) => {
          setToastIsError(true)
          setError(exception?.response?.data?.error)
        })
        .finally(() => {
          setIsLoading(false)
          setToastVisible(true)
          setTimeout(() => {
            window.location.reload()
          }, 3000)
        })
    })
  }

  const showAll = () => {
    setHasAccordionItemsVisible(!hasAccordionItemsVisible)
  }

  const showHGCRelevantContent = () => {
    setHasSpecialElementsVisible(false)
    setHasContentToDoubleCheckItemsVisible(false)
    setHGCRelevantContentVisible(!hgcRelevantContentVisible)
  }

  const showHasSpecialItems = () => {
    setHasContentToDoubleCheckItemsVisible(false)
    setHasSpecialElementsVisible(!hasSpecialElementsVisible)
  }

  const showContentToDoubleCheck = () => {
    setHasSpecialElementsVisible(false)
    setHasContentToDoubleCheckItemsVisible(!hasContentToDoubleCheckItemsVisible)
  }

  const sendRequestsToSuppliers = () => {
    notifySuppliers()
  }

  useEffect(() => {
    setIsLoading(true)
    setTimeout(() => {
      setHasContentToDoubleCheckItemsVisible(false)
      setHasAccordionItemsVisible(false)
      setHasSpecialElementsVisible(false)
      setIsLoading(false)
    }, 400)
  }, [hash])

  return (
    <>
      <CContainer fluid>
        <CRow className='tender-information'>
          <CCol>
            <CCol className='col-md-auto d-sm-flex justify-content-between align-items-center'>
              {tenderData && (
                <div className='d-flex align-items-center'>
                  {!auth.userInfo || !auth.systemSettings ? t('TenderView.DueDateHGCOffers').replace("HGC", "") : (!auth.systemSettings.replaceWordingPerTenderOffice || auth.role === 'devis_admin' ? t('TenderView.DueDateHGCOffers') : t('TenderView.DueDateHGCOffers').replace("HGC", auth.userInfo.departmentsNames[0]))}:
                  <div
                    className={
                      offerDueDatePickerVisible ? 'd-none' : 'ms-2 d-flex align-items-center'
                    }
                  >
                    <strong>
                      {tenderData?.offerDueDate &&
                        dateFormat(tenderData?.offerDueDate, 'dd.mm.yyyy')}
                    </strong>
                    {tenderData?.status !== 'emails_sent' && (
                      <CButton
                        color={tenderData?.offerDueDate === null ? 'danger' : 'link'}
                        onClick={editOfferDueDateHgc}
                      >
                        <CIcon title='Edit Due Date HGC' icon={cilPen} />
                      </CButton>
                    )}
                  </div>
                  <div
                    className={
                      offerDueDatePickerVisible ? 'ms-2 d-flex align-items-center' : 'd-none'
                    }
                  >
                    <CDatePicker
                      locale='de-DE'
                      placeholder={t('SelectDate')}
                      date={tenderData.offerDueDate}
                      onDateChange={(e) => {
                        if (e) {
                          const newd = new Date(e?.getTime() - e?.getTimezoneOffset() * 60000)
                          setNewOfferDueDate(newd.toISOString())
                          updateOfferDueDateHgc(newd.toISOString())
                        } else {
                          setNewOfferDueDate(null)
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              {tenderData && (
                <div className='d-flex align-items-center'>
                  {t('TenderView.DueDateSupplierOffers')}:
                  <div
                    className={
                      supplierDueDatePickerVisible ? 'd-none' : 'ms-2 d-flex align-items-center'
                    }
                  >
                    <strong>
                      {tenderData?.supplierDueDate &&
                        dateFormat(tenderData?.supplierDueDate, 'dd.mm.yyyy')}
                    </strong>
                    {tenderData?.status !== 'emails_sent' && (
                      <CButton
                        color={tenderData?.supplierDueDate === null ? 'danger' : 'link'}
                        onClick={editSupplierDueDate}
                      >
                        <CIcon title='Edit Supplier Date' icon={cilPen} />
                      </CButton>
                    )}
                  </div>
                  <div
                    className={
                      supplierDueDatePickerVisible ? 'ms-2 d-flex align-items-center' : 'd-none'
                    }
                  >
                    <CDatePicker
                      locale='de-DE'
                      date={tenderData?.supplierDueDate}
                      maxDate={tenderData?.offerDueDate}
                      disabled={tenderData?.offerDueDate === null}
                      placeholder={t('SelectDate')}
                      onDateChange={(e) => {
                        if (e) {
                          const newd = new Date(e?.getTime() - e?.getTimezoneOffset() * 60000)
                          setNewSupplierDueDate(newd.toISOString())
                          updateSupplierDueDate(newd.toISOString())
                        } else {
                          setNewSupplierDueDate(null)
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              {tenderData?.uploadedAt && (
                <p className='my-0'>
                  {t('TenderView.UploadDate')}: <strong>{dateFormat(tenderData?.uploadedAt, 'dd.mm.yyyy')}</strong>
                </p>
              )}
            </CCol>
          </CCol>
        </CRow>

        <section
          className={`tender--mainContent ${tenderData?.offerDueDate === null || tenderData?.supplierDueDate === null || userIsOwner === false ? 'disabled' : ''}`}
        >
          <CRow className='tender-actions justify-content-space-between align-items-center'>
            <CCol className='d-flex align-items-center mb-2'>
              {auth.userInfo && auth.systemSettings
                && (!auth.systemSettings.replaceWordingPerTenderOffice || auth.role === 'devis_admin') &&
              (
                <div className='d-flex align-items-center mb-2'>
                  <span className='me-2'>{t('TenderView.ShowAllNpkElements')}</span>
                  <CFormSwitch
                    className='mb-0'
                    label={t('TenderView.HGCRelevantContent')}
                    onChange={showHGCRelevantContent}
                  />
                </div>
              )
            }
            </CCol>
            <CCol className='col-md-auto'>
              <CTooltip
                content={`${t('TenderView.ShowingAsTooltip')} ${showingPreferencesOfUser}`}
                placement="top"
              >
                <CDropdown>
                  <CDropdownToggle
                    size='sm'
                    variant='outline'
                    color='primary'
                    className='mb-2 me-2'
                    disabled={sendRequestsButtonDisabled}
                  >{t('TenderView.ShowWithInheritedFrom')}</CDropdownToggle>
                  <CDropdownMenu style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {usersLoading ?
                      (<CDropdownItem><Spinner /></CDropdownItem>) :
                      (
                        users && users.map(user =>
                          <CDropdownItem
                            key={user.id}
                            onClick={() => inheritFromUser(user.id)}
                            active={user.id === tenderData?.showWithPreferencesOfUserId}
                          >{user.fullName}</CDropdownItem>
                        )
                      )
                    }
                  </CDropdownMenu>
                </CDropdown>
              </CTooltip>
              <CTooltip
                content={t('TenderView.RemoveAssignmentsFromChapter')}
                placement="top"
              >
                <CButton
                  size='sm'
                  variant='outline'
                  color='primary'
                  className='mb-2 me-2'
                  onClick={() => clearAssignmentsOfActiveChapter()}
                  disabled={sendRequestsButtonDisabled}
                >
                  <CIcon icon={cilTrash} />
                </CButton>
              </CTooltip>
              <CButton
                size='sm'
                variant='outline'
                color='primary'
                className='mb-2 me-2'
                onClick={showAll}
                active={hasAccordionItemsVisible}
              >
                {hasAccordionItemsVisible ? t('TenderView.CollapseAllSubChapters') : t('TenderView.ExpandAllSubChapters')}
              </CButton>
              <CButton
                size='sm'
                variant='outline'
                color='primary'
                className='mb-2 me-2'
                onClick={showContentToDoubleCheck}
                active={hasContentToDoubleCheckItemsVisible}
                disabled={hasSpecialElementsVisible}
              >
                {t('TenderView.ContentToDoubleCheck')}
              </CButton>
              <CButton
                size='sm'
                variant='outline'
                color='primary'
                className='mb-2 me-4'
                onClick={showHasSpecialItems}
                active={hasSpecialElementsVisible}
                disabled={hasContentToDoubleCheckItemsVisible}
              >
                {t('TenderView.SpecialElements')}
              </CButton>
              <CButton
                size='sm'
                color='primary'
                className='mb-2'
                disabled={sendRequestsButtonDisabled}
                onClick={() => sendRequestsToSuppliers()}
              >
                {t('TenderView.SendRequestsToSuppliers')}
              </CButton>
            </CCol>
          </CRow>

          <CRow className='nested-accordions'>
            <CCol>
              <Spinner pageLoading={isLoading}>
                {tenderChapters &&
                  tenderChapters
                    .filter((item) => '#' + item.name === hash)
                    .map((data, index) => (
                      <div key={`c-${index}`}>
                        <CollapsibleTree
                          t={t}
                          data={data}
                          tenderStatus={tenderData?.status}
                          hash={hash}
                          hasAccordionItemsVisible={hasAccordionItemsVisible}
                          hasContentToDoubleCheckItemsVisible={hasContentToDoubleCheckItemsVisible}
                          hasSpecialElementsVisible={hasSpecialElementsVisible}
                          hgcRelevantContentVisible={hgcRelevantContentVisible}
                          setActiveChapter={setActiveChapter}
                        />
                      </div>
                    ))}
              </Spinner>
            </CCol>
          </CRow>
        </section>
      </CContainer>
      <FeedbackToast toastVisible={toastVisible} t={t} setToastVisible={setToastVisible} isError={toastIsError} customMessage={customMessage} />
      <CModal
        size="xl"
        backdrop="static"
        visible={changeOwnershipModalVisible}
        onClose={() => setChangeOwnershipModalVisible(false)}
        aria-labelledby="BackdropLabelChangeOnwershipModal"
      >
        <CModalBody>
          {transferOwnershipModalContent.split('\n').map((line, index) => (
            <p key={index}>
              {line}
            </p>
          ))}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" href='/tender-overview' onClick={() => {
            setChangeOwnershipModalVisible(false)
          }}>
            {t('TransferOwnershipModal.Decline')}
          </CButton>
          <CButton color="primary" onClick={() => {
            getTenderOwnwership(tenderId)
          }}>
            {t('TransferOwnershipModal.Accept')}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default memo(TenderView)
